import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import OpsLayout from '../components/OpsLayout';
import '../css/SafePackageList.css';
import { API_BASE_URL } from '../config/config';
import opsStatusTone from '../utils/opsStatusTone';

const paymentStatusLabel = (status) => {
  if (status === 'PAID') return '결제';
  if (status === 'REFUND') return '환불';
  if (status === 'REFUND_REQUESTED') return '환불요청';
  return '미결제';
};

const packageStatusLabel = (status) => {
  if (status === 'ACCEPTED') return '수락됨';
  if (status === 'DENIED') return '거절됨';
  if (status === 'REQUESTED') return '요청중';
  return '상태없음';
};

function SafePackageList() {
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchSafePackageData();
  }, []);

  const fetchSafePackageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('토큰이 없습니다.');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/safe-package-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'success') {
        console.log('안심패키지 데이터 로드 성공:', response.data.data);
        setAllData(response.data.data);
        setFiltered(response.data.data);
      } else {
        console.error('안심패키지 데이터 로드 실패:', response.data.message);
      }
    } catch (error) {
      console.error('안심패키지 데이터 로드 중 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const keyword = search.trim();
    if (!keyword) {
      setFiltered(allData);
      setCurrentPage(1);
      return;
    }
    setFiltered(
      allData.filter(row =>
        row.requester.includes(keyword) ||
        row.requesterContact.includes(keyword) ||
        row.guardian.includes(keyword) ||
        row.guardianContact.includes(keyword)
      )
    );
    setCurrentPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleAccept = async (idx) => {
    const selectedPackage = filtered[(currentPage-1)*rowsPerPage + idx];

    if (!window.confirm(`${selectedPackage.requester}님의 안심패키지 신청을 수락하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('로그인이 필요합니다.');
        return;
      }

      const response = await axios.put(
        `${API_BASE_URL}/api/safe-package/${selectedPackage.package_id}/status`,
        { status: 'ACCEPTED' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'success') {
        alert('패키지가 성공적으로 수락되었습니다.');
        // 데이터 다시 로드
        fetchSafePackageData();
      } else {
        alert('수락 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('수락 처리 오류:', error);
      alert('수락 처리 중 오류가 발생했습니다.');
    }
  };

  const handleReject = async (idx) => {
    const selectedPackage = filtered[(currentPage-1)*rowsPerPage + idx];

    if (!window.confirm(`${selectedPackage.requester}님의 안심패키지 신청을 거절하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('로그인이 필요합니다.');
        return;
      }

      const response = await axios.put(
        `${API_BASE_URL}/api/safe-package/${selectedPackage.package_id}/status`,
        { status: 'DENIED' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'success') {
        alert('패키지가 성공적으로 거절되었습니다.');
        // 데이터 다시 로드
        fetchSafePackageData();
      } else {
        alert('거절 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('거절 처리 오류:', error);
      alert('거절 처리 중 오류가 발생했습니다.');
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const pagedData = filtered.slice((currentPage-1)*rowsPerPage, currentPage*rowsPerPage);
  const handlePrev = () => setCurrentPage(p => Math.max(1, p-1));
  const handleNext = () => setCurrentPage(p => Math.max(1, Math.min(totalPages, p+1)));

  return (
    <OpsLayout title="안심패키지 신청 조회 및 수락" description="안심패키지 신청을 확인하고 수락하거나 거절해요.">
      <section className="ops-page__card safe-package-page">
        <div className="sds-toolbar">
          <input
            className="sds-input safe-package-page__search"
            placeholder="이름 또는 연락처로 검색"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button className="sds-btn sds-btn--secondary" onClick={handleSearch}>검색</button>
        </div>

        {loading ? (
          <div className="sds-notice">데이터를 불러오는 중...</div>
        ) : (
          <div className="sds-table-wrap">
            <table className="sds-table">
              <thead>
                <tr>
                  <th>신청일자</th>
                  <th>요청자</th>
                  <th>보호자</th>
                  <th>결제 상태</th>
                  <th>상태</th>
                  <th className="sds-table__center">처리</th>
                </tr>
              </thead>
              <tbody>
                {pagedData.length > 0 ? (
                  pagedData.map((row, idx) => {
                    const payLabel = paymentStatusLabel(row.paymentStatus);
                    const stLabel = packageStatusLabel(row.status);
                    return (
                      <tr key={row.package_id ?? idx}>
                        <td>{row.date}</td>
                        <td>
                          <b className="safe-package-page__name">{row.requester}</b>
                          <small className="safe-package-page__sub">{row.requesterContact}</small>
                        </td>
                        <td>
                          <span className="safe-package-page__name">{row.guardian}</span>
                          <small className="safe-package-page__sub">{row.guardianContact}</small>
                        </td>
                        <td>
                          <span className={`sds-badge sds-badge--${opsStatusTone(payLabel)}`}>{payLabel}</span>
                        </td>
                        <td>
                          <span className={`sds-badge sds-badge--${opsStatusTone(stLabel)}`}>{stLabel}</span>
                        </td>
                        <td className="sds-table__center">
                          <div className="sds-row-actions">
                            <button className="sds-btn sds-btn--weak sds-btn--sm" onClick={() => handleAccept(idx)}>수락</button>
                            <button className="sds-btn sds-btn--secondary sds-btn--sm" onClick={() => handleReject(idx)}>거절</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="sds-empty">검색 결과가 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="sds-pagination">
          <button
            className="sds-pagination__btn"
            onClick={handlePrev}
            disabled={currentPage === 1}
            aria-label="이전 페이지"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`sds-pagination__btn${currentPage === page ? ' is-active' : ''}`}
              aria-current={currentPage === page ? 'page' : undefined}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="sds-pagination__btn"
            onClick={handleNext}
            disabled={currentPage >= totalPages}
            aria-label="다음 페이지"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      </section>
    </OpsLayout>
  );
}

export default SafePackageList;
